// Every ducted metric the enrichment stamps onto a compatible-combination row,
// read in one place, so the Choose Systems panel, the rail card, and the
// manufactured SystemOption the comparison surfaces show all see the same
// numbers. Three groups:
//
// 1. Operating cost (annual cooling / heating / total dollars) with the basis
//    each side was estimated on. The total also rides the schema's
//    totalAnnualCost, which every existing cost surface already reads.
// 2. Bin coverage — hours-weighted percent of annual load-hours covered plus
//    the outdoor temperature through which the system holds full output.
// 3. Design-load coverage — rated capacity against the design load, the
//    sizing check the quote is filtered on. Nested on a saved system, flat
//    columns on a row.
import type {
  DuctedCoverageBasis,
  DuctedCoverageType
} from 'utils/ducted-coverage';

// Equipment the customer keeps has no model number, so its efficiency is
// taken from the specs they recorded rather than a rating.
export type DuctedEstimateBasis = 'rated' | 'assumed_kept_equipment';

export type DuctedOperatingCost = {
  annualCoolingCost: number | null;
  annualHeatingCost: number | null;
  // The heat the system cannot deliver, priced as the space heaters that
  // make it up (electric resistance at the electricity rate). Zero for a
  // system that carries every heating hour; part of the total, and its own
  // line on the card when it is more than zero.
  annualSupplementalHeatingCost: number | null;
  annualTotalCost: number | null;
  coolingBasis: DuctedEstimateBasis | null;
  heatingBasis: DuctedEstimateBasis | null;
};

export type DuctedBinCoverage = {
  heatingCoveragePercent: number | null;
  coolingCoveragePercent: number | null;
  minTempAtFullHeating: number | null;
  maxTempAtFullCooling: number | null;
};

function readFiniteNumber(
  source: Record<string, unknown>,
  key: string
): number | null {
  const value = source[key];
  if (value == null || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function readNonNegativeNumber(
  source: Record<string, unknown>,
  key: string
): number | null {
  const parsed = readFiniteNumber(source, key);
  return parsed === null || parsed < 0 ? null : parsed;
}

function readPositiveNumber(
  source: Record<string, unknown>,
  key: string
): number | null {
  const parsed = readFiniteNumber(source, key);
  return parsed === null || parsed <= 0 ? null : parsed;
}

// The server caps every coverage percent at 100; a float artefact just past
// it is still a full 100%, not a figure that failed to compute.
function readPercent(
  source: Record<string, unknown>,
  key: string
): number | null {
  const percent = readNonNegativeNumber(source, key);
  return percent === null ? null : Math.min(percent, 100);
}

function readEstimateBasis(value: unknown): DuctedEstimateBasis | null {
  return value === 'rated' || value === 'assumed_kept_equipment' ? value : null;
}

// Null when the row carries nothing of the estimate — the enrichment nulls
// every field together whenever the climate lookup or the efficiency ratings
// fall short, and an all-null object would make a saved system claim an
// estimate exists. The total is the server's rolled-up figure (rounded to
// cents there), never re-derived here.
export function readDuctedOperatingCost(
  combination: Record<string, unknown>
): DuctedOperatingCost | null {
  const cost: DuctedOperatingCost = {
    annualCoolingCost: readNonNegativeNumber(
      combination,
      'estimatedAnnualCoolingCost'
    ),
    annualHeatingCost: readNonNegativeNumber(
      combination,
      'estimatedAnnualHeatingCost'
    ),
    annualSupplementalHeatingCost: readNonNegativeNumber(
      combination,
      'estimatedAnnualSupplementalHeatingCost'
    ),
    annualTotalCost: readNonNegativeNumber(
      combination,
      'estimatedAnnualOperatingCost'
    ),
    coolingBasis: readEstimateBasis(combination.coolingEstimateBasis),
    heatingBasis: readEstimateBasis(combination.heatingEstimateBasis)
  };
  return Object.values(cost).every((value) => value === null) ? null : cost;
}

export function readDuctedBinCoverage(
  combination: Record<string, unknown>
): DuctedBinCoverage | null {
  const coverage: DuctedBinCoverage = {
    heatingCoveragePercent: readPercent(combination, 'heatingCoveragePercent'),
    coolingCoveragePercent: readPercent(combination, 'coolingCoveragePercent'),
    minTempAtFullHeating: readFiniteNumber(combination, 'minTempAtFullHeating'),
    maxTempAtFullCooling: readFiniteNumber(combination, 'maxTempAtFullCooling')
  };
  return Object.values(coverage).every((value) => value === null)
    ? null
    : coverage;
}

const ESTIMATE_BASES: ReadonlySet<DuctedCoverageBasis> = new Set([
  'tonnage_estimate',
  'simplified_estimate'
]);

// Anything but a known estimate basis is the project's own design loads.
function readDuctedCoverageBasis(value: unknown): DuctedCoverageBasis {
  return typeof value === 'string' &&
    ESTIMATE_BASES.has(value as DuctedCoverageBasis)
    ? (value as DuctedCoverageBasis)
    : 'design_loads';
}

// Design-load coverage reaches a combination row as flat columns (the row is
// a loose bag of SQL fields), unlike the nested object a saved system holds.
// The requirement the percents divide by rides along, so a row carrying a
// load but no computable percent (a capacity the row lacks) still says what
// it was matched against; null only when the row carries neither.
export function readDuctedDesignCoverage(
  combination: Record<string, unknown>
): DuctedCoverageType | null {
  const heatingCoveragePercent = readPercent(
    combination,
    'designLoadHeatingCoveragePercent'
  );
  const coolingCoveragePercent = readPercent(
    combination,
    'designLoadCoolingCoveragePercent'
  );
  const heatingRequirementBtu = readPositiveNumber(
    combination,
    'designLoadHeatingRequirementBtu'
  );
  const coolingRequirementBtu = readPositiveNumber(
    combination,
    'designLoadCoolingRequirementBtu'
  );
  if (
    heatingCoveragePercent === null &&
    coolingCoveragePercent === null &&
    heatingRequirementBtu === null &&
    coolingRequirementBtu === null
  ) {
    return null;
  }
  return {
    heatingCoveragePercent,
    coolingCoveragePercent,
    basis: readDuctedCoverageBasis(combination.designLoadCoverageBasis),
    heatingRequirementBtu,
    coolingRequirementBtu
  };
}

// Saying so is the difference between a number the customer can lean on and
// one they cannot.
export function estimateSublabel(basis: DuctedEstimateBasis | null): string {
  return basis === 'assumed_kept_equipment'
    ? '(Estimated for equipment you are keeping)'
    : '(Estimated)';
}

// The supplemental-heat figure as a card line: the amount when the system
// leaves heat for space heaters to make up, null when it carries every hour
// (nothing to say) or no estimate ran.
export function supplementalHeatingCostLine(
  operatingCost: DuctedOperatingCost | null | undefined
): number | null {
  const cost = operatingCost?.annualSupplementalHeatingCost ?? null;
  return cost !== null && cost > 0 ? cost : null;
}

// A total built on one kept-equipment side has to say so.
export function includesKeptEquipmentEstimate(
  operatingCost: DuctedOperatingCost | null | undefined
): boolean {
  return (
    operatingCost?.coolingBasis === 'assumed_kept_equipment' ||
    operatingCost?.heatingBasis === 'assumed_kept_equipment'
  );
}

export const KEPT_EQUIPMENT_COST_NOTE = 'Includes equipment you are keeping';
